// DepHealthTile — one tile per platform dependency on the NOC home page.
// Phase 1G leftover #1.
//
// Visual contract:
//   * dep name (big, mono-ish), status chip, latency in tabular nums.
//   * "Last checked" relative time on a dim secondary line.
//   * On hover/focus the message (if any) is surfaced via title= for SR.
//
// Premium aesthetic: borrows the metric-card primitives from the home page
// so tiles align cleanly into the existing grid without a custom layout.
import { Database, GitBranch, Layers, Search, Server, Send, Shield, Workflow } from "lucide-react";

import { type DepResult, statusChipClass, statusLabel } from "~/lib/health";

const ICON_BY_NAME: Record<string, React.ComponentType<{ size?: number }>> = {
    postgres:   Database,
    opensearch: Search,
    clickhouse: Layers,
    neo4j:      GitBranch,
    vault:      Shield,
    kafka:      Send,
    minio:      Server,
    temporal:   Workflow,
};

const LABEL_BY_NAME: Record<string, string> = {
    postgres:   "PostgreSQL",
    opensearch: "OpenSearch",
    clickhouse: "ClickHouse",
    neo4j:      "Neo4j",
    vault:      "Vault",
    kafka:      "Kafka",
    minio:      "MinIO",
    temporal:   "Temporal",
};

function relativeTime(iso: string): string {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return "—";
    const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
    if (sec < 60) return `${sec}s ago`;
    const m = Math.floor(sec / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
}

export interface DepHealthTileProps {
    dep: DepResult;
}

export function DepHealthTile({ dep }: DepHealthTileProps): React.ReactElement {
    const Icon = ICON_BY_NAME[dep.name] ?? Server;
    const label = LABEL_BY_NAME[dep.name] ?? dep.name;
    const chipClass = statusChipClass(dep.status);
    return (
        <div
            className="metric-card"
            title={dep.message ? `${statusLabel(dep.status)} — ${dep.message}` : statusLabel(dep.status)}
        >
            <span className="metric-card__label">
                <span className="metric-card__label__icon">
                    <Icon size={12} />
                </span>
                {label}
            </span>
            <span className="cluster-xs" style={{ alignItems: "baseline" }}>
                <span className={`chip ${chipClass}`}>{statusLabel(dep.status)}</span>
                <span className="text-13 tabular-nums text-text-muted">
                    {dep.status === "not_configured" ? "—" : `${dep.latencyMs} ms`}
                </span>
            </span>
            <span className="metric-card__sub">
                {dep.status === "not_configured"
                    ? (dep.message ?? "Not configured")
                    : `Checked ${relativeTime(dep.checkedAt)}`}
            </span>
        </div>
    );
}
